import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { t } from '@lingui/macro';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';
import { openPostOnRedirect, setGiggReferrer } from '../utils';
import BragifyEmbedItem from './BragifyEmbedItem';
import IntlProvider from '../components/IntlProvider';
import styles from './BragifyEmbed.module.css';
import '../iconSetup';
import {
  AnalyticsEvents,
  EmbedTypes,
  AnalyticsScores,
  AnalyticsPollTime,
  AnalyticsMaxPollTime,
} from '../consts';
import ModalEmbed from '../components/Modal/ModalEmbed';
import { ModalProvider } from '../components/ModalProvider/ModalProvider';

const { REACT_APP_CORE_API_URL } = process.env;
const { REACT_APP_GIGG_ANALYTICS_KEY } = process.env;
const { REACT_APP_ENV } = process.env;

// TODO: break out the code that request posts and shows loading animatino
// into its own parent component and lazy load the rest when posts are fetched
// to reduce entry script size.

class BragifyEmbed extends Component {
  constructor(props) {
    Modal.setAppElement(`#${props.domElementId}`);
    super(props);
    this.state = {
      posts: [],
      totalPages: 0,
      postsFlipped: 0,
      getParams: {
        page: 1,
      },
    };
    const { config } = props;
    const preview = config.preview ? 'preview/' : '';

    this.apiURL = `${REACT_APP_CORE_API_URL}/fanboard/${preview}${config.accountId}/${config.folderId}`;

    this.inViewPrev = false;

    window.giggAnalyticsLoaded.then(async () => {
      setGiggReferrer();
      window.giggAnalytics = new GiggAnalytics(REACT_APP_GIGG_ANALYTICS_KEY, {
        apiVersion: 1,
        env: REACT_APP_ENV,
        sessionKey: __gigg.folderId,
      });
      window.giggFirstEvent = await giggAnalytics.logEvent(AnalyticsEvents.EMBED_PAGE_VIEW, {
        boardId: config.accountId,
        boardType: EmbedTypes.GRID,
        folderId: __gigg.folderId,
      });
      const interval = setInterval(() => {
        giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW_POLL, {
          boardId: __gigg.accountId,
          boardType: EmbedTypes.GRID,
          folderId: __gigg.folderId,
          firstEvent: {
            _id: window.giggFirstEvent?._id,
            createdDate: window.giggFirstEvent?.createdDate,
          },
          inViewEvent: {
            _id: window.latestViewEvent?._id,
            createdDate: window.latestViewEvent?.createdDate,
          },
          isPoll: true,
          basePollFilter: 'firstEvent._id',
        });
      }, AnalyticsPollTime);
      setTimeout(() => clearInterval(interval), AnalyticsMaxPollTime);
    });
  }

  componentDidMount() {
    const { getParams } = this.state;
    const { openModal, config, getReferredPost } = this.props;
    const optionsUrl = `${this.apiURL}/options`;
    this.setState({ loading: true });
    fetch(optionsUrl)
      .then((response) => response.json())
      .then((options) => {
        const { loadCount = 20 } = options;
        const url = `${this.apiURL}/${getParams.page}?size=${loadCount}`;
        fetch(url)
          .then((response) => response.json())
          .then((result) => {
            const { totalPages, posts } = result;
            this.setState({
              posts,
              totalPages,
              requestFailed: !posts.length,
              loading: false,
              options,
            });
            openPostOnRedirect(posts, openModal, getReferredPost, config, EmbedTypes.GRID);
          })
          .catch(() => {
            this.setState({ requestFailed: true, loading: false });
          });
      })
      .catch(() => {
        this.setState({ requestFailed: true, loading: false });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    const { getParams, posts: statePosts, options: stateOptions } = this.state;
    if (getParams.page > prevState.getParams.page) {
      const url = `${this.apiURL}/${getParams.page}?size=${stateOptions.loadCount || 20}`;

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        loading: true,
      });

      fetch(url)
        .then((response) => response.json())
        .then((result) => {
          const { totalPages, posts, options } = result;
          this.setState({
            posts: [...statePosts, ...posts],
            totalPages,
            endOfFeed: totalPages === getParams.page,
            loading: false,
            options,
          });
        })
        .catch(() => {
          this.setState({
            endOfFeed: true,
            loading: false,
          });
        });
    }
  }

  getMorePosts = (e) => {
    e.preventDefault();
    this.setState((prevState) => ({
      getParams: { ...prevState.getParams, page: prevState.getParams.page + 1 },
    }));
    giggAnalytics.logEvent(AnalyticsEvents.SEE_MORE, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.GRID,
      folderId: __gigg.folderId,
      scoreWeight: AnalyticsScores.SEE_MORE,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
      inViewEvent: {
        _id: window.latestViewEvent?._id,
        createdDate: window.latestViewEvent?.createdDate,
      },
    });
  };

  renderLoadBtn = () => {
    const { endOfFeed, loading, totalPages, options } = this.state;
    const { i18n } = this.props;

    if (endOfFeed || !options.showMoreBtn) return null;

    const loadMoreClasses = classNames([styles.btn, styles['btn-load-more']], {
      [styles.show]: totalPages > 1,
    });

    const btnText = loading ? i18n._(t`Loading...`) : i18n._(t`See More`);

    return (
      <button
        className={loadMoreClasses}
        onClick={this.getMorePosts}
        disabled={loading}
        type="button"
      >
        {btnText}
      </button>
    );
  };

  handleMediaError = (post, err) => {
    const { posts } = this.state;
    if (err.target.nodeName === 'VIDEO') {
      const newPosts = [];
      posts.forEach((p) => {
        if (post.id === p.id) {
          newPosts.push({ ...p, media: post.media.map((item) => ({ ...item, video: undefined })) });
        } else {
          newPosts.push(p);
        }
      });
      this.setState({ posts: newPosts });
    } else {
      const newPosts = posts.filter((p) => post.id !== p.id);
      this.setState({ posts: newPosts });
    }
  };

  handleInViewChange = async (inView) => {
    if (!inView && this.inViewPrev) {
      this.inViewPrev = inView;
      await giggAnalytics.logEvent(AnalyticsEvents.EMBED_UNVIEW, {
        boardId: __gigg.accountId,
        boardType: EmbedTypes.GRID,
        folderId: __gigg.folderId,
        firstEvent: {
          _id: window.giggFirstEvent?._id,
          createdDate: window.giggFirstEvent?.createdDate,
        },
        inViewEvent: {
          _id: window.latestViewEvent?._id,
          createdDate: window.latestViewEvent?.createdDate,
        },
      });
      window.latestViewEvent = {};
      return window.latestViewEvent;
    }
    if (!inView) {
      return null;
    }
    this.inViewPrev = inView;
    window.latestViewEvent = await giggAnalytics.logEvent(AnalyticsEvents.EMBED_VIEW, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.GRID,
      folderId: __gigg.folderId,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
    });
    return window.latestViewEvent;
  };

  handlePostFlipped = () => {
    const { postsFlipped } = this.state;
    let curPostsFlipped = postsFlipped;
    curPostsFlipped += 1;
    this.setState({ postsFlipped: curPostsFlipped });
  };

  render() {
    const { requestFailed, posts, options, postsFlipped } = this.state;
    const modalRepeat = false;
    const embedType = EmbedTypes.GRID;
    const {
      config,
      formatDistanceLocale,
      showPostMedia,
      handleModalNavigate,
      selectedPost,
      modalIsOpen,
      openModal,
      closeModal,
      referredPost,
    } = this.props;

    if (requestFailed) return null;

    if (!posts.length) {
      return (
        <div className={styles.spinner}>
          <div className={styles.spinningCircle} />
        </div>
      );
    }

    return (
      <InView as="div" onChange={this.handleInViewChange}>
        <div style={{ background: options.secondaryColor }} role="complementary">
          <div className={styles['bragify-layout-grid']}>
            {posts.map((post, i) => (
              <BragifyEmbedItem
                data={post}
                index={i}
                key={post.id}
                openModal={openModal}
                accountId={config.accountId}
                options={options}
                onMediaError={this.handleMediaError}
                formatDistanceLocale={formatDistanceLocale}
                postsFlipped={postsFlipped}
                handlePostFlipped={this.handlePostFlipped}
                embedType={embedType}
              />
            ))}
          </div>
          <div className={styles['load-more-wrapper']}>{this.renderLoadBtn()}</div>
          <ModalEmbed
            referredPost={referredPost}
            selectedPost={selectedPost}
            boardId={config.accountId}
            showPostMedia={showPostMedia}
            formatDistanceLocale={formatDistanceLocale}
            closeModal={closeModal}
            options={options}
            itemCount={posts.length}
            onNavigate={handleModalNavigate}
            modalIsOpen={modalIsOpen}
            config={config}
            posts={posts}
            modalRepeat={modalRepeat}
            embedType={embedType}
          />
        </div>
      </InView>
    );
  }
}

ReactDOM.render(
  <IntlProvider>
    {({ i18n, formatDistanceLocale }) => (
      <ModalProvider>
        {({
          showPostMedia,
          handleModalNavigate,
          selectedPost,
          modalIsOpen,
          openModal,
          closeModal,
          referredPost,
          getReferredPost,
        }) => (
          <BragifyEmbed
            config={window.__gigg}
            domElementId={window.__gigg.domElementId}
            i18n={i18n}
            formatDistanceLocale={formatDistanceLocale}
            showPostMedia={showPostMedia}
            handleModalNavigate={handleModalNavigate}
            selectedPost={selectedPost}
            modalIsOpen={modalIsOpen}
            openModal={openModal}
            closeModal={closeModal}
            referredPost={referredPost}
            getReferredPost={getReferredPost}
          />
        )}
      </ModalProvider>
    )}
  </IntlProvider>,
  document.getElementById(window.__gigg.domElementId)
);
